import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Lock, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { formatINR } from "../lib/format";

// Always-visible monthly milestone progress: a step chart across all tiers
// (3 through 20 agreements). FLAT and capped, not cumulative — reaching a
// higher tier tops the total up to that tier's bonus, it doesn't stack with
// lower tiers already reached. Shows the current cycle's locked balance and
// days left until it unlocks. Cycle is anchored to the user's own
// account-creation date, not calendar-month-aligned.
export default function MilestoneProgressWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevAchieved = useRef(null);

  useEffect(() => {
    api.get("/milestones/current")
      .then(({ data }) => {
        // Toast for any tier that newly flipped to achieved since the last
        // fetch in this session (session-local only — no backend "seen" flag).
        if (prevAchieved.current) {
          for (const tier of data.tiers) {
            const wasAchieved = prevAchieved.current[tier.threshold];
            if (tier.achieved && !wasAchieved) {
              toast.success(`🎉 Milestone reached! ${tier.threshold} agreements — ${formatINR(tier.bonus_paise)} total locked this cycle (unlocks in ${data.days_remaining_in_cycle} days)`);
            }
          }
        }
        prevAchieved.current = Object.fromEntries(data.tiers.map((t) => [t.threshold, t.achieved]));
        setData(data);
      })
      .catch((e) => console.error("Failed to load milestone progress:", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const nextTier = data.tiers.find((t) => !t.achieved);

  return (
    <div className="rounded-xl bg-gray-100 border border-gray-200 p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-gray-500 shrink-0" />
          <span className="text-sm font-semibold text-gray-900">Monthly milestone bonus</span>
        </div>
        <span className="text-xs text-gray-500">{data.days_remaining_in_cycle} day{data.days_remaining_in_cycle === 1 ? "" : "s"} left this cycle</span>
      </div>

      {/* Step chart */}
      <div className="flex items-center overflow-x-auto">
        {data.tiers.map((tier, i) => (
          <React.Fragment key={tier.threshold}>
            {i > 0 && (
              <div className={`flex-1 h-0.5 ${data.tiers[i - 1].achieved ? "bg-emerald-400" : "bg-gray-300"}`} />
            )}
            <div className="flex flex-col items-center shrink-0">
              <div
                className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                  tier.achieved
                    ? "bg-emerald-500 border-emerald-500 text-white"
                    : tier === nextTier
                      ? "bg-white border-[#F97316] text-[#F97316] animate-pulse"
                      : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {i === data.tiers.length - 1 ? "🏆" : tier.achieved ? "✓" : tier.threshold}
              </div>
              <div className="text-[10px] text-gray-500 mt-1 text-center">
                {tier.threshold}<br />{formatINR(tier.bonus_paise)}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className="text-xs text-gray-600">
        {data.agreements_count} agreement{data.agreements_count === 1 ? "" : "s"} this cycle
        {nextTier && (
          <> — <span className="font-semibold text-gray-900">{Math.max(0, nextTier.threshold - data.agreements_count)} more</span> to reach {formatINR(nextTier.bonus_paise)} total for this cycle</>
        )}
      </div>

      {data.locked_balance_paise > 0 && (
        <div className="flex items-center gap-2 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
          <Lock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
          <span>
            <span className="font-semibold text-gray-900">{formatINR(data.locked_balance_paise)}</span> earned this cycle —
            unlocks to your wallet in {data.days_remaining_in_cycle} day{data.days_remaining_in_cycle === 1 ? "" : "s"}
          </span>
        </div>
      )}
    </div>
  );
}
